import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';

const roleConfig = {
  notary:     { label: 'Notario',    bg: '#EEF0FB', color: '#4F63D2', perms: 'Acceso total al sistema' },
  supervisor: { label: 'Supervisor', bg: '#DBEAFE', color: '#1D4ED8', perms: 'Todos los clientes y expedientes, sin config.' },
  staff:      { label: 'Staff',      bg: '#F3F4F6', color: '#374151', perms: 'Solo sus propios clientes y expedientes' },
};

const inputSt = { width: '100%', padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#111827', backgroundColor: '#fff', outline: 'none', fontFamily: 'inherit' };
const focusSt = e => { e.target.style.borderColor = '#4F63D2'; e.target.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.1)'; };
const blurSt  = (e, err) => { e.target.style.borderColor = err ? '#EF4444' : '#E5E7EB'; e.target.style.boxShadow = 'none'; };

const Label = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
    {children} {required && <span style={{ color: '#EF4444' }}>*</span>}
  </label>
);

const Field = ({ label, required, error, children }) => (
  <div style={{ marginBottom: 14 }}>
    <Label required={required}>{label}</Label>
    {children}
    {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#EF4444' }}>{error}</p>}
  </div>
);

const IconBtn = ({ onClick, title, variant = 'default', children }) => {
  const v = { default: ['#F4F6FB', '#4B5563', '#E5E7EB'], primary: ['#EEF0FB', '#4F63D2', '#dde1f7'], danger: ['#FEE2E2', '#EF4444', '#fecaca'] }[variant];
  return (
    <button onClick={onClick} title={title}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 7, border: 'none', cursor: 'pointer', backgroundColor: v[0], color: v[1], flexShrink: 0 }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = v[2]}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = v[0]}>
      {children}
    </button>
  );
};

const EditIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const emptyForm = { name: '', email: '', password: '', role: 'staff' };

const Users = () => {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors]       = useState({});
  const [form, setForm]           = useState(emptyForm);

  const fetchUsers = async () => {
    try { const res = await api.get('/users'); setUsers(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleChange = e => { setForm({ ...form, [e.target.name]: e.target.value }); setErrors({ ...errors, [e.target.name]: null }); };

  const handleNew    = () => { setEditing(null); setForm(emptyForm); setErrors({}); setShowForm(true); };
  const handleEdit   = u  => { setEditing(u); setForm({ name: u.name, email: u.email, password: '', role: u.role }); setErrors({}); setShowForm(true); };
  const handleCancel = () => { setShowForm(false); setEditing(null); setForm(emptyForm); setErrors({}); };

  const handleSubmit = async e => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) await api.put(`/users/${editing.id}`, form);
      else await api.post('/users', form);
      toast.success(editing ? 'Usuario actualizado' : 'Usuario creado');
      handleCancel(); fetchUsers();
    } catch (error) {
      if (error.response?.status === 422) setErrors(error.response.data.errors);
      else toast.error('Ocurrió un error al guardar');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async u => {
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success('Usuario eliminado'); fetchUsers(); }
    catch (error) { toast.error(error.response?.data?.message || 'Error al eliminar'); }
  };

  return (
    <div>
      <PageHeader
        title="Usuarios"
        subtitle="Gestiona los usuarios y sus roles de acceso"
        actions={
          <button onClick={handleNew}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d4fb8'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#4F63D2'}>
            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo Usuario
          </button>
        }
      />

      {/* Formulario inline */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="15" height="15" fill="none" stroke="#4F63D2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
              {editing ? 'Editar Usuario' : 'Nuevo Usuario'}
            </span>
          </div>
          <div style={{ padding: 20 }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 20px' }}>
                <Field label="Nombre" required error={errors.name?.[0]}>
                  <input type="text" name="name" value={form.name} onChange={handleChange} required
                    style={{ ...inputSt, borderColor: errors.name ? '#EF4444' : '#E5E7EB' }}
                    onFocus={focusSt} onBlur={e => blurSt(e, !!errors.name)} />
                </Field>
                <Field label="Correo electrónico" required error={errors.email?.[0]}>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required
                    style={{ ...inputSt, borderColor: errors.email ? '#EF4444' : '#E5E7EB' }}
                    onFocus={focusSt} onBlur={e => blurSt(e, !!errors.email)} />
                </Field>
                <Field label={editing ? 'Nueva Contraseña (vacío = no cambiar)' : 'Contraseña'} required={!editing} error={errors.password?.[0]}>
                  <input type="password" name="password" value={form.password} onChange={handleChange} required={!editing}
                    style={{ ...inputSt, borderColor: errors.password ? '#EF4444' : '#E5E7EB' }}
                    onFocus={focusSt} onBlur={e => blurSt(e, !!errors.password)} />
                </Field>
                <Field label="Rol" required>
                  <select name="role" value={form.role} onChange={handleChange}
                    style={{ ...inputSt, cursor: 'pointer' }} onFocus={focusSt} onBlur={e => blurSt(e, false)}>
                    <option value="staff">Staff — Solo sus clientes y expedientes</option>
                    <option value="supervisor">Supervisor — Todos los clientes y expedientes</option>
                    <option value="notary">Notario — Acceso total</option>
                  </select>
                </Field>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button type="submit" disabled={submitting}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Guardando...' : 'Guardar'}
                </button>
                <button type="button" onClick={handleCancel}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tabla de usuarios */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
            <div style={{ width: 28, height: 28, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          </div>
        ) : users.length === 0 ? (
          <EmptyState icon="👤" title="No hay usuarios registrados" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB' }}>
                {['Usuario', 'Correo', 'Rol', 'Permisos', ''].map((h, i) => (
                  <th key={i} className={[1, 3].includes(i) ? 'mobile-hide' : ''}
                    style={{ padding: '10px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const rc = roleConfig[u.role] || roleConfig.staff;
                return (
                  <tr key={u.id}
                    style={{ borderBottom: '1px solid #F9FAFB' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', backgroundColor: '#EEF0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#4F63D2', fontSize: 13, flexShrink: 0 }}>
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{u.name}</div>
                          <div className="d-md-none" style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="mobile-hide" style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280' }}>{u.email}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, backgroundColor: rc.bg, color: rc.color }}>
                        {rc.label}
                      </span>
                    </td>
                    <td className="mobile-hide" style={{ padding: '12px 16px', fontSize: 12, color: '#9CA3AF' }}>{rc.perms}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                        <IconBtn onClick={() => handleEdit(u)} title="Editar" variant="default">
                          <EditIcon />
                        </IconBtn>
                        <IconBtn onClick={() => handleDelete(u)} title="Eliminar" variant="danger">
                          <TrashIcon />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Users;
